import { DotsCheckbox } from "dots-ui/dotsCheckbox";
import { FormikProps } from "formik";
import _ from "lodash";
import moment from "moment";
import React, { useContext, useMemo } from "react";
import {
  EventReportRow,
  SubmitEventReport,
} from "../../../../../../generated/data-contracts";
import { AvisPrimaryButton } from "../../../../../shared/components/buttons";
import {
  AvisEditableList,
  IAvisEditableListFieldConfig,
  IRowState,
} from "../../../../../shared/components/editableList";
import {
  AvisCalendar,
  AvisInputDecimal,
  AvisInputInt,
  AvisInputText,
} from "../../../../../shared/components/inputs";
import { AvisContext } from "../../../../../shared/context";

interface IReportClientEventsFormEditableListProps {
  bag: FormikProps<SubmitEventReport>;
  bagField: keyof SubmitEventReport;
  disabled: boolean;
  loading: boolean;
  periodFrom: Date | undefined;
  periodTo: Date | undefined;

  setIsConfirmSaveModalOpenForRowIndex: React.Dispatch<
    React.SetStateAction<number | undefined>
  >;
  navigateToProgramReport: (licenseClientEventId?: number) => void;
}

export function ReportClientEventsFormEditableList(
  props: Readonly<IReportClientEventsFormEditableListProps>,
) {
  const {
    bag,
    bagField,
    disabled,
    loading,
    periodFrom,
    periodTo,
    setIsConfirmSaveModalOpenForRowIndex,
    navigateToProgramReport,
  } = props;
  const { res } = useContext(AvisContext);

  const hideClear = true;
  const yearTo = periodFrom?.getFullYear();
  const yearFrom = periodTo?.getFullYear();
  const yearRangeStr = `${yearFrom}:${yearTo}`;

  const rowInitValue = useMemo(() => {
    return {
      eventName: "",
      eventDate: "",
    };
  }, []);

  const fieldCollection = useMemo((): IAvisEditableListFieldConfig<
    IRowState<EventReportRow>
  >[] => {
    return [
      {
        name: "eventDate",
        title: res("label_date"),
        dataType: "Date",
        required: true,
        minWidth: 95,
        width: 95,
        renderField: (field, prefix, index, value, onChange) => {
          return (
            <AvisCalendar
              name={`${prefix}.${index}.${field.name}`}
              value={
                value.eventDate ? moment(value.eventDate).toDate() : undefined
              }
              disabled={disabled}
              onChange={onChange}
              minDate={periodFrom}
              maxDate={periodTo}
              yearRange={yearRangeStr}
              defaultDateToShow={periodFrom}
              required={field.required}
              hideClear
            />
          );
        },
      },
      {
        name: "eventName",
        title: res("label_Title"),
        required: true,
        minWidth: 150,
        width: 500,
        renderField: (field, prefix, index, value, onChange) => {
          return (
            <AvisInputText
              name={`${prefix}.${index}.${field.name}`}
              value={value[field.name]?.toString()}
              maxLength={255}
              onChange={onChange}
              disabled={disabled}
              hideClear={hideClear}
            />
          );
        },
      },
      {
        name: "withEntryFee", //Used only for UI element
        title: res("licensees_LicenseReports_type2_label_WithEntranceFee"),
        width: 75,
        forceEditMode: true,
        renderField: (field, prefix, index, value, onChange) => {
          return (
            <DotsCheckbox
              name={`${prefix}.${index}.${field.name}`}
              className="license-report-event-checkbox-column"
              spacing="small"
              checked={value.withEntryFee}
              simpleInput
              onChange={onChange}
              disabled={
                disabled ||
                !value.rowState ||
                value.rowState === "View" ||
                value.rowState === "Delete"
              }
            />
          );
        },
      },
      {
        name: "soldTickets",
        title: res("licensees_LicenseReports_type2_label_SoldTickets"),
        shouldUpdateOnRender: true,
        width: 100,
        renderField: (field, prefix, index, value, onChange) => {
          return (
            <AvisInputInt
              name={`${prefix}.${index}.${field.name}`}
              value={value.withEntryFee ? value[field.name]?.toString() : ""}
              onChange={onChange}
              disabled={disabled || !value.withEntryFee}
              hideClear={hideClear}
            />
          );
        },
      },
      {
        name: "grossRevenue",
        title: res("licensees_LicenseReports_type2_label_GrossRevenue"),
        dataType: "Double",
        shouldUpdateOnRender: true,
        width: 100,
        renderField: (field, prefix, index, value, onChange) => {
          return (
            <AvisInputDecimal
              name={`${prefix}.${index}.${field.name}`}
              value={value.withEntryFee ? value[field.name]?.toString() : ""}
              onChange={onChange}
              disabled={disabled || !value.withEntryFee}
              hideClear={hideClear}
            />
          );
        },
      },
      {
        name: "totalInvitations",
        title: res("licensees_LicenseReports_type2_label_TotalInvitations"),
        shouldUpdateOnRender: true,
        width: 100,
        renderField: (field, prefix, index, value, onChange) => {
          return (
            <AvisInputInt
              name={`${prefix}.${index}.${field.name}`}
              value={value.withEntryFee ? value[field.name]?.toString() : ""}
              onChange={onChange}
              disabled={disabled || !value.withEntryFee}
              hideClear={hideClear}
            />
          );
        },
      },
      {
        name: "totalVisitorsWithoutTicket",
        title: res(
          "licensees_LicenseReports_type2_label_TotalVisitorsWithoutTicket",
        ),
        width: 150,
        shouldUpdateOnRender: true,
        renderField: (field, prefix, index, value, onChange) => {
          return (
            <AvisInputInt
              name={`${prefix}.${index}.${field.name}`}
              value={!value.withEntryFee ? value[field.name]?.toString() : ""}
              onChange={onChange}
              disabled={disabled || value.withEntryFee}
              hideClear={hideClear}
            />
          );
        },
      },
      {
        name: "portal_LicenseProgramReportId",
        forceEditMode: true,
        width: 200,
        renderField: (field, prefix, index, value) => {
          return (
            <AvisPrimaryButton
              name={`${prefix}.${index}.${field.name}`}
              spacing="small"
              variant={
                value.isEmpty || !value.portal_LicenseProgramReportId
                  ? "outlined"
                  : "filled"
              }
              className="license-report-button-pp"
              // style={{ minWidth: "200px", width: "100%" }}
              label={res("licensees_LicenseReports_shared_label_ProgramReport")}
              disabled={value.rowState === "Delete"}
              onClick={async () => {
                const isChanged = !_.isEqual(bag.values, bag.initialValues);
                if (isChanged && !disabled) {
                  const errors = await bag.validateForm();
                  if (Object.keys(errors).length > 0) {
                    //Has invalid/incomplete data - can not redirect
                    setIsConfirmSaveModalOpenForRowIndex(undefined);

                    return;
                  }
                  setIsConfirmSaveModalOpenForRowIndex(index);

                  return;
                }
                navigateToProgramReport(value.licenseClientEventId);
              }}
            />
          );
        },
      },
    ];
  }, [
    disabled,
    periodFrom,
    periodTo,
    yearRangeStr,
    res,
    hideClear,
    bag,
    navigateToProgramReport,
    setIsConfirmSaveModalOpenForRowIndex,
  ]);

  if (bag.values.eventNotOccuredPortal) return null;

  return (
    <AvisEditableList<SubmitEventReport, EventReportRow>
      bag={bag}
      bagField={bagField}
      initValue={rowInitValue}
      fields={fieldCollection}
      confirmBeforeDelete={(data) => {
        if (JSON.stringify(data) === JSON.stringify(rowInitValue))
          return undefined;

        return {
          content:
            data.isEmpty === false
              ? res("message_DeleteRow_PP")
              : res("message_DeleteRow"),
          mustShow: true,
        };
      }}
      disabled={disabled}
      loading={loading}
    />
  );
}
